import type { HttpErrorResponse } from '@angular/common/http';

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ISortEventArgs,
  TSortDirections,
} from 'src/app/shared/classes/grid-page-info';
import { IProviderGroup } from 'src/app/shared/interfaces/provider/provider-group.interfaces';
import {
  IProvider,
  ProviderFilters,
} from 'src/app/shared/interfaces/provider/provider.interfaces';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ProviderGQLService } from 'src/app/shared/services/provider/provider.graphql.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';

@Component({
  selector: 'providers-group-grid',
  templateUrl: './providers-group-grid.component.html',
  styles: [':host { height: 100%; overflow: hidden;}'],
})
export class ProvidersGroupGridComponent implements OnDestroy, OnInit {
  @Input() set groups(value: IProviderGroup[]) {
    this.groupsDTO = value.map((x) => {
      return { ...x };
    });
  }
  @Input() sortDirections: TSortDirections;
  @Input() moreRowsExists = false;
  @Input() headers: ProvidersGroupGridHeadersOptions = {};

  @Output() edit = new EventEmitter<IProviderGroup>();
  @Output() delete = new EventEmitter<IProviderGroup>();
  @Output() groupScroll = new EventEmitter();
  @Output() sort = new EventEmitter();

  @Output() providerCreate = new EventEmitter<IProviderEditArgs>();
  @Output() providerEdit = new EventEmitter<IProviderEditArgs>();
  @Output() providerDelete = new EventEmitter<IProvider>();

  groupsDTO: IProviderGroupDTO[];
  isViewOnly: boolean;
  isClientAdmin?: boolean;
  actionText: string;
  private subscriptions: Subscription = new Subscription();

  loadingGroupId?: string;

  constructor(
    private srvGqlProviders: ProviderGQLService,
    private alertService: AlertService,
    private userStateService: UserStateService,
  ) {}

  ngOnInit(): void {
    const currentUser = this.userStateService.user;
    const permissions = currentUser?.permissions;
    const containsAccounts = permissions?.some((permission) => {
      return (
        permission.name === 'Accounts' ||
        permission.name === 'Client Admin - Accounts'
      );
    });
    this.isClientAdmin = permissions?.some((permission) => {
      return permission.name === 'Client Admin - Accounts';
    });
    this.isViewOnly = !containsAccounts;
    this.actionText = 'Edit Provider Group';
    if (!containsAccounts) {
      this.actionText = 'View Provider Group';
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  doEdit(group?: IProviderGroup): void {
    if (group) {
      this.edit.emit(group);
    }
  }

  doDelete(group?: IProviderGroup): void {
    if (group) {
      this.delete.emit(group);
    }
  }

  doScroll() {
    this.groupScroll.emit();
  }

  callOnSort(sortBy: ISortEventArgs) {
    this.sort.emit(sortBy);
  }

  doProviderCreate(group: IProviderGroup, provider?: IProvider) {
    if (provider) {
      this.providerCreate.emit({ group, provider });
    }
  }

  providerEditComplete(group: IProviderGroup) {
    group.providers = undefined;
    this.loadingGroupId = group.groupId;

    const filter: ProviderFilters = { groupId: group.groupId };

    this.subscriptions.add(
      this.srvGqlProviders
        .getProviders({
          filter,
          orderby: 'id',
          direction: 'ASC',
        })
        .subscribe({
          next: (p) => {
            this.loadingGroupId = undefined;
            group.providers = p.nodes;
          },
          error: (error: unknown) => {
            this.loadingGroupId = undefined;
            this.alertService.error(error as HttpErrorResponse | string);
          },
        }),
    );
  }

  doProviderDelete(provider?: IProvider) {
    if (provider) {
      this.providerDelete.emit(provider);
    }
  }

  onProvidersSort(sort: ISortEventArgs, pgroup: IProviderGroup) {
    if (pgroup.providers && pgroup.providers.length > 0) {
      pgroup.providers.sort((a, b) => {
        if (sort.direction == 'ASC') {
          return (a[sort.column] ?? '') < (b[sort.column] ?? '') ? -1 : 1;
        } else {
          return (a[sort.column] ?? '') > (b[sort.column] ?? '') ? -1 : 1;
        }
      });
    }
  }
}

export class IProviderEditArgs {
  group: IProviderGroup;
  provider?: IProvider;
}

export type ProvidersGroupGridHeadersOptions = {
  disableAdd?: boolean;
  groupId?: string;
  name?: string;
};

type IProviderGroupDTO = {
  isExpanded?: boolean;
} & IProviderGroup;
